import { Component, Input, OnChanges, SimpleChanges, OnDestroy, inject, ChangeDetectionStrategy } from '@angular/core';
import { VehicleWarning } from 'src/app/modules/shared/models';
import { WarningsService } from 'src/app/modules/core/services/warning.service';
import { WarningIconComponent } from '../../../modules/shared/components/warning-icon/warning-icon.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { TranslateModule } from '@ngx-translate/core';
import { MomentModule } from 'ngx-moment';

export enum SignOnWarningState {
    Initial = 0,
    Loading = 1,
    Success = 2,
    Error = 3
}

@Component({
    selector: 'app-signon-warning',
    templateUrl: './signon-warning.component.html',
    styleUrls: ['./signon-warning.component.scss'],
    imports: [WarningIconComponent, MatProgressSpinnerModule, TranslateModule, MomentModule],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class SignOnWarningComponent implements OnChanges, OnDestroy {
    private readonly _warningService = inject(WarningsService);

    @Input() warning: VehicleWarning;
    @Input() inventoryNumber: string;
    @Input() routeNumber: string;
    @Input() tripNumber: string;
    @Input() hastusRegion: string;
    @Input() trafficDate: string;


    public state: SignOnWarningState = SignOnWarningState.Initial;
    public minutesSinceWarning: number = 0;
    public errorMessage: string = '';

    protected readonly SignOnWarningState = SignOnWarningState;

    private _intervalTimer: any;
    private _waitingForData: boolean = false;


    constructor() { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['warning']?.firstChange === false ||
            changes['inventoryNumber']?.firstChange === false) {
            this.state = SignOnWarningState.Initial;
        }

        // If we're waiting for data and it just arrived, proceed with sign-on
        if (this._waitingForData && this._isDataReady()) {
            this._waitingForData = false;
            this._executeSignOn();
        }
    }

    ngOnDestroy(): void {
        if (this._intervalTimer) {
            clearInterval(this._intervalTimer);
        }
    }

    public async signOn(): Promise<void> {
        if (this.state !== SignOnWarningState.Initial && this.state !== SignOnWarningState.Error) return;

        this.state = SignOnWarningState.Loading;

        if (this._isDataReady()) {
            await this._executeSignOn();
        } else {
            // Wait for data with timeout
            this._waitForDataWithTimeout();
        }
    }

    private _isDataReady(): boolean {
        return !!this.hastusRegion && !!this.routeNumber && !!this.tripNumber && !!this.trafficDate;
    }

    private _waitForDataWithTimeout(): void {
        this._waitingForData = true;
        const timeoutMs = 7000;
        const checkIntervalMs = 100;
        let elapsed = 0;

        const checkInterval = setInterval(() => {
            elapsed += checkIntervalMs;

            if (this._isDataReady()) {
                clearInterval(checkInterval);
                this._waitingForData = false;
                this._executeSignOn();
            } else if (elapsed >= timeoutMs) {
                clearInterval(checkInterval);
                this._waitingForData = false;
                this.state = SignOnWarningState.Error;
                this.errorMessage = 'Unable to sign on. Is the vehicle in traffic?';
            }
        }, checkIntervalMs);
    }

    private async _executeSignOn(): Promise<void> {
        const messageId = this.warning?.metadata?.messageId;

        const result = await this._warningService.signOnRequest(
            messageId,
            this.hastusRegion,
            this.routeNumber,
            this.tripNumber,
            this.trafficDate
        );

        if (result.success) {
            this.state = SignOnWarningState.Success;
        } else {
            this.state = SignOnWarningState.Error;
            this.errorMessage = result.errorMessage || 'Unknown error';
        }
    }

}